import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle, Clock, ThumbsUp, Award } from 'lucide-react';

export const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const reasons = [
    {
      icon: Award,
      title: 'Trusted Since 2015',
      description: 'Nearly a decade of proven excellence and customer satisfaction.',
    },
    {
      icon: CheckCircle,
      title: 'Professional Team',
      description: 'Experienced professionals led by qualified management.',
    },
    {
      icon: Clock,
      title: 'Timely Service',
      description: 'We respect your time with prompt and reliable service delivery.',
    },
    {
      icon: ThumbsUp,
      title: 'Customer Focused',
      description: 'Your satisfaction is our top priority in everything we do.',
    },
  ];

  return (
    <section ref={ref} className="py-10 sm:py-14 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Why Choose <span className="gradient-text-blue">VTV Enterprises</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Experience the difference of working with a company that truly cares about your success.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="metallic-card p-6 rounded-xl text-center group hover-lift"
              >
                <div className="flex flex-col items-center space-y-4">
                  <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors animate-pulse-slow">
                    <Icon className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{reason.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 max-w-3xl mx-auto glass-card p-8 sm:p-12 rounded-2xl"
        >
          <h3 className="text-2xl font-bold text-center mb-8">Our Journey</h3>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold">2015</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Foundation</h4>
                <p className="text-sm text-muted-foreground">
                  VTV Enterprises established by Mr. Varnam Vaheesan with a vision for excellence.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold">2020</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Expansion</h4>
                <p className="text-sm text-muted-foreground">
                  Extended our services across Sri Lanka with enhanced offerings and customer support.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-accent font-bold">2025</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Modern Era</h4>
                <p className="text-sm text-muted-foreground">
                  Embracing digital transformation while maintaining our commitment to personalized service.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
